import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { AppButton, AppInput, AppSelect } from "components/antd-ui";
import { memo, useCallback, type ChangeEvent } from "react";
import type { OpportunityFile } from "types/opportunity";
import { OpportunityFiles } from "views/Opportunity/components/OpportunityFiles";
import { documentLabelPresets, ORDER_UPLOAD_URL, OTHER_DOCUMENT_LABEL } from "../order.meta";

export type DocumentRow = {
  key: string;
  id?: number;
  label: string;
  files: OpportunityFile[];
};

type OrderDocumentsProps = {
  rows: DocumentRow[];
  readOnly?: boolean;
  onChange: (rows: DocumentRow[]) => void;
};

const presetOptions = [
  ...documentLabelPresets.map((label) => ({ value: label, label })),
  { value: OTHER_DOCUMENT_LABEL, label: OTHER_DOCUMENT_LABEL },
];

const isPreset = (label: string) => documentLabelPresets.includes(label);

export const createDocumentRow = (): DocumentRow => ({
  key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  label: documentLabelPresets[0],
  files: [],
});

const DocumentItem = memo(
  ({
    row,
    readOnly,
    onPatch,
    onRemove,
  }: {
    row: DocumentRow;
    readOnly?: boolean;
    onPatch: (key: string, patch: Partial<DocumentRow>) => void;
    onRemove: (key: string) => void;
  }) => {
    const custom = !isPreset(row.label);
    const selectValue = custom ? OTHER_DOCUMENT_LABEL : row.label;

    const handleTypeChange = useCallback(
      (value: string) => {
        onPatch(row.key, { label: value === OTHER_DOCUMENT_LABEL ? "" : value });
      },
      [onPatch, row.key],
    );

    const handleCustomChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onPatch(row.key, { label: event.target.value });
      },
      [onPatch, row.key],
    );

    const handleFilesChange = useCallback(
      (files: OpportunityFile[]) => {
        onPatch(row.key, { files });
      },
      [onPatch, row.key],
    );

    const remove = useCallback(() => onRemove(row.key), [onRemove, row.key]);

    return (
      <div className="orderDocuments__row">
        <div className="orderDocuments__label">
          <AppSelect
            value={selectValue}
            options={presetOptions}
            onChange={handleTypeChange}
            disabled={readOnly}
          />
          {custom && (
            <AppInput
              value={row.label}
              onChange={handleCustomChange}
              disabled={readOnly}
              placeholder="Nhập loại hồ sơ"
            />
          )}
        </div>
        <OpportunityFiles
          value={row.files}
          onChange={handleFilesChange}
          readOnly={readOnly}
          uploadUrl={ORDER_UPLOAD_URL}
        />
        {!readOnly && (
          <button type="button" className="orderDocuments__remove" onClick={remove} aria-label="Xóa hồ sơ">
            <DeleteOutlined />
          </button>
        )}
      </div>
    );
  },
);

DocumentItem.displayName = "DocumentItem";

export const OrderDocuments = memo(({ rows, readOnly, onChange }: OrderDocumentsProps) => {
  const onPatch = useCallback(
    (key: string, patch: Partial<DocumentRow>) => {
      onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    },
    [onChange, rows],
  );

  const onRemove = useCallback(
    (key: string) => {
      onChange(rows.filter((row) => row.key !== key));
    },
    [onChange, rows],
  );

  const addRow = useCallback(() => {
    onChange([...rows, createDocumentRow()]);
  }, [onChange, rows]);

  return (
    <div className="orderDocuments">
      {rows.map((row) => (
        <DocumentItem key={row.key} row={row} readOnly={readOnly} onPatch={onPatch} onRemove={onRemove} />
      ))}
      {!readOnly && (
        <AppButton className="productTable__add" icon={<PlusOutlined />} onClick={addRow}>
          Thêm hồ sơ
        </AppButton>
      )}
    </div>
  );
});

OrderDocuments.displayName = "OrderDocuments";
